import React from "react";
import { DisclosureGroup } from "react-aria-components";
import { jt, t } from "ttag";

import type { SpecificationsBlock as Value } from "@reactivated";

import { Themed } from "../../styles/themes";
import { NEXT_HEADING } from "../AccordionBlock";
import { DisclosureRow } from "../AccordionBlock/DisclosureRow";
import { Svg } from "../Svg";

import styles from "./index.module.scss";

type WarrantyItem = NonNullable<
    NonNullable<Value["warranty"]>["items"]
>[number];

const renderWarrantyItem = (item: WarrantyItem): React.ReactNode => {
    if (item.type === "parts_link") {
        const link = <a href="https://lgparts.com">LGparts.com</a>;
        return <>{jt`To order replacement parts, visit ${link}`}</>;
    }
    // preset terms arrive pre-resolved from the backend
    return item.value || null;
};

export const renderableWarrantyItems = (
    items: WarrantyItem[],
): { id: string; content: React.ReactNode }[] =>
    items
        .map((item) => ({ id: item.id, content: renderWarrantyItem(item) }))
        .filter((entry) => entry.content !== null);

const CheckmarkItem = ({ children }: { children: React.ReactNode }) => (
    <li className={styles.checkmarkItem}>
        <Svg aria-hidden="true" name="check" className={styles.check} />
        <span>{children}</span>
    </li>
);

export const SpecificationsBlock = ({ value }: { value: Value }) => {
    const features = value.features?.items ?? [];
    const dimensionRows = value.dimensions?.rows ?? [];
    const warrantyItems = value.warranty?.items ?? [];
    const accessoryGroups = value.accessories?.groups ?? [];

    const renderedWarranty = renderableWarrantyItems(warrantyItems);

    const hasAnyPanel =
        features.length > 0 ||
        dimensionRows.length > 0 ||
        renderedWarranty.length > 0 ||
        accessoryGroups.length > 0;
    if (!hasAnyPanel) return <></>;

    const HeadingTag = "h2";
    const rowHeadingTag = NEXT_HEADING[HeadingTag];
    const AccessoryHeadingTag = NEXT_HEADING[rowHeadingTag];

    return (
        <Themed theme={value.theme} tag="section">
            <div className={styles.inner}>
                <HeadingTag className={styles.heading}>
                    {t`Specifications`}
                </HeadingTag>
                <DisclosureGroup allowsMultipleExpanded className={styles.list}>
                    {features.length > 0 && (
                        <DisclosureRow
                            title={t`Features`}
                            headingTag={rowHeadingTag}
                        >
                            <ul className={styles.featuresList}>
                                {features.map((item, i) => (
                                    <CheckmarkItem key={i}>
                                        {item}
                                    </CheckmarkItem>
                                ))}
                            </ul>
                        </DisclosureRow>
                    )}
                    {dimensionRows.length > 0 && (
                        <DisclosureRow
                            title={t`Dimensions`}
                            headingTag={rowHeadingTag}
                        >
                            <dl className={styles.dimensions}>
                                {dimensionRows.map((row, i) => (
                                    <div
                                        key={i}
                                        className={styles.dimensionRow}
                                    >
                                        <dt className={styles.dimensionLabel}>
                                            {row.label}
                                        </dt>
                                        <dd>{row.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </DisclosureRow>
                    )}
                    {renderedWarranty.length > 0 && (
                        <DisclosureRow
                            title={t`Warranty & Maintenance`}
                            headingTag={rowHeadingTag}
                        >
                            <ul className={styles.warrantyList}>
                                {renderedWarranty.map(({ id, content }) => (
                                    <li key={id}>{content}</li>
                                ))}
                            </ul>
                        </DisclosureRow>
                    )}
                    {accessoryGroups.length > 0 && (
                        <DisclosureRow
                            title={t`Accessories`}
                            headingTag={rowHeadingTag}
                        >
                            <div className={styles.accessoryGroups}>
                                {accessoryGroups.map((group, i) => (
                                    <div
                                        key={i}
                                        className={styles.accessoryGroup}
                                    >
                                        <AccessoryHeadingTag
                                            className={styles.accessoryHeading}
                                        >
                                            {group.heading}
                                        </AccessoryHeadingTag>
                                        <ul className={styles.accessoryList}>
                                            {(group.items ?? []).map(
                                                (item, j) => (
                                                    <CheckmarkItem key={j}>
                                                        {item}
                                                    </CheckmarkItem>
                                                ),
                                            )}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </DisclosureRow>
                    )}
                </DisclosureGroup>
            </div>
        </Themed>
    );
};
